import React, { useState, useEffect } from 'react';

const CELEBRATION_KEY = 'kdu-checkin-celebrated';
const CONFETTI_COUNT = 60;
const CONFETTI_COLORS = ['#1a6b3c', '#c9a84c', '#e6e0f3', '#4a3080', '#16a34a', '#f59e0b', '#ef4444', '#3b82f6'];

interface ConfettiPiece {
  id: number;
  left: number;
  delay: number;
  duration: number;
  color: string;
  size: number;
  rotation: number;
}

function generateConfetti(): ConfettiPiece[] {
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.8,
    duration: 2 + Math.random() * 2,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    size: 6 + Math.random() * 8,
    rotation: Math.random() * 360,
  }));
}

/**
 * Shows a confetti celebration + banner when the user is checked in.
 * Only fires once per session (uses sessionStorage).
 */
export function CheckInCelebration({ isCheckedIn }: { isCheckedIn: boolean }) {
  const [show, setShow] = useState(false);
  const [confetti] = useState<ConfettiPiece[]>(generateConfetti);

  useEffect(() => {
    if (isCheckedIn && !sessionStorage.getItem(CELEBRATION_KEY)) {
      sessionStorage.setItem(CELEBRATION_KEY, 'true');
      setShow(true);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => setShow(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isCheckedIn]);

  if (!show) return null;

  return (
    <>
      {/* Confetti overlay */}
      <div style={styles.confettiContainer} aria-hidden="true">
        {confetti.map((piece) => (
          <div
            key={piece.id}
            style={{
              position: 'absolute',
              left: `${piece.left}%`,
              top: '-10px',
              width: `${piece.size}px`,
              height: `${piece.size * 0.6}px`,
              backgroundColor: piece.color,
              borderRadius: '2px',
              transform: `rotate(${piece.rotation}deg)`,
              animation: `confettiFall ${piece.duration}s ease-in ${piece.delay}s forwards`,
              opacity: 0.9,
            }}
          />
        ))}
      </div>

      {/* Celebration banner */}
      <div style={styles.banner} role="alert">
        <span style={styles.bannerEmoji}>🎉</span>
        <div style={styles.bannerText}>
          <strong>You're checked in!</strong>
          <span style={styles.bannerSub}>Welcome to the Legacy Ball — enjoy the evening!</span>
        </div>
      </div>

      {/* Keyframe animation */}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes bannerSlideIn {
          0% { transform: translateY(-100%); opacity: 0; }
          15% { transform: translateY(0); opacity: 1; }
          85% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-100%); opacity: 0; }
        }
      `}</style>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  confettiContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 9999,
    overflow: 'hidden',
  },
  banner: {
    position: 'fixed',
    top: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 24px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(26, 107, 60, 0.3)',
    zIndex: 10000,
    animation: 'bannerSlideIn 5s ease-in-out forwards',
    maxWidth: '90vw',
  },
  bannerEmoji: {
    fontSize: '1.5rem',
  },
  bannerText: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  bannerSub: {
    fontSize: '0.8rem',
    opacity: 0.9,
    fontWeight: 400,
  },
};
